import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { take } from 'rxjs/operators';

import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { WindowService } from 'src/shared/providers/window.service';
import { DemoAccountService, AddressAssignedItems, AssignedActionItem } from 'src/shared/services/demo-account.service';
import { AccountManagerService } from 'src/shared/services/account-manager.service';
import { OnboardingStepNavigationService } from 'src/shared/services/onboarding-step-navigation.service';
import { RentalModesService } from 'src/shared/services/rental-modes.service';
import { ListingsSetupComponent } from 'src/shared/components/listings-setup/listings-setup.component';
import { Util } from 'src/shared/util/util';

/**
 * "My Account" page opened from the floating account-manager pill.
 *
 * - The account manager (name, photo, call / text / schedule actions) lives in a card at
 *   the top; once a setup call is scheduled the card shows a "Setup call scheduled" note.
 * - Below it, Account Settings toggles which rental businesses the account runs
 *   (short-term / long-term). These gate whole feature sets — see RentalModesService.
 * - Once the user has added a property, each address is shown (yellow/green dot + name)
 *   with any concierge-assigned items and a "Tell Us About Your Listings" item that opens
 *   the listings setup flow (every address × channel on one page). Below the addresses we
 *   note that the account manager determines the rest on the call.
 * - Before any property exists, a "Your First Property" placeholder still surfaces the
 *   "Tell Us About Your Listings" and "Subscribe" entries; the listings flow then asks for
 *   a property first, and subscribing is account-wide so it needs none.
 */
@Component({
  selector: 'onboarding-status',
  templateUrl: './onboarding-status.component.html',
  styleUrls: ['./onboarding-status.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class OnboardingStatusComponent implements OnInit {

  // Every address on the account (each with its concierge-assigned items, possibly empty).
  addresses: AddressAssignedItems[] = [];
  callScheduled = false;
  setupCallCompleted = false;
  // Account-wide "Subscribe" done — hides the placeholder's Subscribe item before any
  // property exists (per-address items already fold this in).
  subscribeCompleted = false;
  isLoaded = false;

  // Concierge-set target go-live date, pre-formatted for display (e.g. "Aug 15, 2026"), or
  // null when unset. When present, it shows as a row in the account-manager card below the
  // "Setup call scheduled with …" line.
  targetGoLiveDateDisplay: string | null = null;

  // Addresses whose completed items are currently revealed (collapsed by default).
  private expandedCompleted = new Set<number>();

  // The customer's assigned account manager (from /me), or TIDY's default until one is set.
  managerName = AccountManagerService.DEFAULT.name;
  managerPhoto = AccountManagerService.DEFAULT.photoUrl;
  managerEmail = AccountManagerService.DEFAULT.email;
  managerPhone = AccountManagerService.DEFAULT.phone;

  // ---- Account Settings ----
  // Which rental businesses this account runs. Turning one off hides its whole
  // feature set (long-term: Leasing, Leases, Rent Collection, Screenings…).
  shortTermRentals = true;
  longTermRentals = true;

  windowService = inject(WindowService);
  private demoAccountService = inject(DemoAccountService);
  private accountManagerService = inject(AccountManagerService);
  private rightSidePanelService = inject(RightSidePanelService);
  private stepNavigation = inject(OnboardingStepNavigationService);
  private rentalModes = inject(RentalModesService);
  private util = inject(Util);
  private cdr = inject(ChangeDetectorRef);

  async ngOnInit(): Promise<void> {
    this.rightSidePanelService.setDialogPageTitle('My Account');
    const modes = this.rentalModes.modes;
    this.shortTermRentals = modes.shortTerm;
    this.longTermRentals = modes.longTerm;
    this.rightSidePanelService.setDialogLoading(true);
    try {
      const [callScheduled, callCompleted, manager, targetGoLiveDate, subscribeCompleted] = await Promise.all([
        this.demoAccountService.isSetupCallScheduled(),
        this.demoAccountService.isSetupCallCompleted(),
        this.accountManagerService.getDisplay(),
        this.demoAccountService.getTargetGoLiveDate(),
        this.demoAccountService.isSubscribeStepCompleted(),
      ]);
      this.callScheduled = callScheduled;
      this.setupCallCompleted = callCompleted;
      this.subscribeCompleted = subscribeCompleted;
      this.targetGoLiveDateDisplay = this.demoAccountService.formatGoLiveDate(targetGoLiveDate);
      this.managerName = manager.name;
      this.managerPhoto = manager.photoUrl;
      this.managerEmail = manager.email;
      this.managerPhone = manager.phone;
      await this.loadAssignedItems();
    } finally {
      this.rightSidePanelService.setDialogLoading(false);
      this.isLoaded = true;
      this.cdr.markForCheck();
    }
  }

  private async loadAssignedItems(): Promise<void> {
    try {
      // Every address (not just those with assigned items) so a freshly-added property shows.
      this.addresses = await this.demoAccountService.getAssignedActionItemsByAddress();
    } catch {
      this.addresses = [];
    }
    this.cdr.markForCheck();
  }

  get hasAddress(): boolean {
    return this.addresses.length > 0;
  }

  // ---- Account Settings toggles ----
  // Each writes through immediately (no save button): RentalModesService pushes the
  // change to every listener, so the gated feature sets show/hide live.

  onShortTermRentalsChange(): void {
    this.shortTermRentals = !this.shortTermRentals;
    this.rentalModes.update({ shortTerm: this.shortTermRentals });
    this.cdr.markForCheck();
  }

  onLongTermRentalsChange(): void {
    this.longTermRentals = !this.longTermRentals;
    this.rentalModes.update({ longTerm: this.longTermRentals });
    this.cdr.markForCheck();
  }

  // The "Your account manager will determine any additional action items on your call."
  // note shows until the account manager has actually assigned items (i.e. while no
  // address has any concierge-assigned items) — both for the placeholder and after an
  // address is added.
  get showDetermineNote(): boolean {
    // Once the call is done the note is moot — the manager has had the call.
    return !this.setupCallCompleted && this.addresses.every((a) => a.items.length === 0);
  }

  // Show a "Tell Us About Your Listings" item under an address until the flow has been
  // completed for it (a completed step surfaces as a done 'listings' item instead). Shown
  // regardless of the chosen setup-call goal.
  showConnectListingsFor(address: AddressAssignedItems): boolean {
    return !address.items.some((i) => i.stepId === 'listings');
  }

  // Show a "Subscribe" item under an address until the client subscribes (a completed
  // 'subscribe' item surfaces as a done item instead). Like the listings item, this is a
  // default client step that always appears — no concierge assignment required.
  showSubscribeFor(address: AddressAssignedItems): boolean {
    return !address.items.some((i) => i.stepId === 'subscribe');
  }

  // Completed items are collapsed behind a "View Completed Items" link per address.
  hasCompletedItems(address: AddressAssignedItems): boolean {
    return address.items.some((i) => i.done);
  }

  isCompletedExpanded(address: AddressAssignedItems): boolean {
    return this.expandedCompleted.has(address.addressId);
  }

  toggleCompleted(address: AddressAssignedItems): void {
    if (this.expandedCompleted.has(address.addressId)) {
      this.expandedCompleted.delete(address.addressId);
    } else {
      this.expandedCompleted.add(address.addressId);
    }
    this.cdr.markForCheck();
  }

  // Open an assigned (concierge) item's onboarding step; re-read on close so it flips to done.
  startItem = (address: AddressAssignedItems, item: AssignedActionItem) => {
    this.stepNavigation.openStep(item.stepId, address.addressId, () => {
      this.loadAssignedItems();
    });
  };

  // Open the Subscribe step (lands on the subscribe page); re-read on close so it flips to
  // done. The subscription is account-wide, so the no-property placeholder opens it without
  // an address.
  startSubscribe = (address?: AddressAssignedItems) => {
    this.stepNavigation.openStep('subscribe', address?.addressId, async () => {
      this.subscribeCompleted = await this.demoAccountService.isSubscribeStepCompleted();
      this.loadAssignedItems();
    });
  };

  // Open Tell Us About Your Listings. The page covers every property, so the same flow
  // completes the item for all addresses at once; re-read on close so it flips to done.
  openListingsSetup = (addressId?: number) => {
    this.rightSidePanelService.setDialogPageTitle('Tell Us About Your Listings');
    // navigateTo → swaps within the desktop panel, full-page route on mobile.
    this.rightSidePanelService.navigateTo('listings-setup', { addressId }, ListingsSetupComponent);
    this.rightSidePanelService.afterPanelClosed().pipe(take(1)).subscribe(() => {
      this.loadAssignedItems();
    });
  };

  // Account manager actions (inlined from the former account-manager modal).
  email = () => {
    window.location.href = `mailto:${this.managerEmail}`;
  };

  text = () => {
    window.location.href = `sms:${this.managerPhone}`;
  };

  schedule = () => {
    this.util.openUrl(DemoAccountService.SCHEDULE_CALL_URL);
  };
}
